import React, { useRef } from 'react';
import styles from './OutstandingReceivables.module.scss';
import { useScopedTranslation } from '../../../application/localization/useScopedTranslation.ts';
import {
  BooleanParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'react-router-url-params';
import {
  pagingSortingQueryParams,
  pagingSortingToBackendRequest,
} from '../../../helpers/pagination-helper.ts';
import { useAdvancedForm } from '../../../helpers/form/useAdvancedForm.ts';
import { useWatch } from 'react-hook-form';
import { DataGridRef, Pager, Scrolling } from 'devextreme-react/data-grid';
import {
  DXG,
  useDataGrid,
} from '../../../components/devexpress/Grid/dx-grid.ts';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { Loading } from '../../../components/uikit/suspense/Loading.tsx';
import { Checkbox } from '../../../components/uikit/inputs/Checkbox.tsx';
import { Input } from '../../../components/uikit/inputs/Input.tsx';
import { Button } from '../../../components/uikit/buttons/Button.tsx';
import clsx from 'clsx';
import { NegativeActionText } from '../../../components/uikit/NegativeActionText.tsx';
import { IconButton } from '../../../components/uikit/IconButton.tsx';
import Cross from 'assets/icons/close-button-small.svg?react';
import gridStyles from '../../../components/devexpress/Grid/grid.module.scss';
import formStyles from '../../../components/form/form.module.scss';
import {
  onContextMenuPreparing,
  TableHeaderCell,
} from '../../../helpers/tableHeaderCell/TableHeaderCell.tsx';
import { headerSortClick } from '../../../helpers/sort-helper.ts';
import { AppPagination } from '../../../components/uikit/pagination/AppPagination.tsx';
import { OutstandingReceivablesQuery } from 'services/api/api-client.ts';
import { getColumnsConfig, Total } from './components/helpers.tsx';
import { isPast } from 'date-fns';
import { TablePlaceholder } from '../../../components/uikit/table/TablePlaceholder.tsx';
import { abasUnavailableUrn, ErrObj } from '../../../helpers/abas-helper.ts';
import { AbasPlaceholder } from '../../../components/uikit/AbasPlaceholder.tsx';

type Form = {
  invoiceNumber: string;
  debt: boolean;
  isOverdueDebt: boolean;
  credited: boolean;
  repaid: boolean;
};

const pageSizes = [10, 20, 50];

const Open = 'open';
const InvoiceAmount = 'invoiceAmount';
const DueDate = 'dueDate';

export const Debt = 'Debt';
export const Repaid = 'Repaid';
export const Credited = 'Credited';

export const OverdueDebt = 'OverdueDebt';

export const OutstandingReceivables = () => {
  const i18n = useScopedTranslation('Page.OutstandingReceivables');

  const [params, setParams] = useQueryParams({
    invoiceNumber: StringParam,
    isOverdueDebt: withDefault(BooleanParam, false),
    credited: withDefault(BooleanParam, false),
    repaid: withDefault(BooleanParam, false),
    debt: withDefault(BooleanParam, false),
    ...pagingSortingQueryParams(pageSizes[0]),
    sortBy: withDefault(StringParam, 'dueDate'),
    desc: withDefault(BooleanParam, true),
  });

  const form = useAdvancedForm<Form>(
    async (data) => {
      setParams({ ...params, ...data, page: 1 });
    },
    {
      defaultValues: {
        invoiceNumber: params.invoiceNumber ?? '',
        debt: params.debt ?? false,
        isOverdueDebt: params.isOverdueDebt ?? false,
        credited: params.credited ?? false,
        repaid: params.repaid ?? false,
      },
    },
  );

  const isResetButtonShown =
    !!params.invoiceNumber ||
    !!params.debt ||
    !!params.isOverdueDebt ||
    !!params.credited ||
    !!params.repaid;

  const isAllOptionsDisabled =
    !params.debt && !params.isOverdueDebt && !params.credited && !params.repaid;

  const outstandingReceivablesData = OutstandingReceivablesQuery.useGetQuery(
    {
      invoiceNumber: !!params.invoiceNumber ? params.invoiceNumber : undefined,
      debt: !isAllOptionsDisabled ? params.debt : undefined,
      credited: !isAllOptionsDisabled ? params.credited : undefined,
      repaid: !isAllOptionsDisabled ? params.repaid : undefined,
      isOverdueDebt: !isAllOptionsDisabled ? params.isOverdueDebt : undefined,
      ...pagingSortingToBackendRequest(params),
    },
    {
      throwOnError: false,
      staleTime: 30 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  const data = outstandingReceivablesData.data;
  const generalStatus = data?.generalStatus ?? '';
  const totalSum = data?.totalSum ?? 0;
  const numberOfRows = data?.records.length ?? 0;
  const totalCount = data?.countOfRecords ?? 0;

  const [invoiceNumber, debt, isOverdueDebt, repaid, credited] = useWatch({
    control: form.control,
    name: ['invoiceNumber', 'debt', 'isOverdueDebt', 'repaid', 'credited'],
  });

  const changePagination = (pagination: {
    page?: number;
    perPage?: number;
  }) => {
    setParams((prevParams) => ({
      ...prevParams,
      ...(pagination.page !== undefined && { page: pagination.page }),
      ...(pagination.perPage !== undefined && { perPage: pagination.perPage }),
    }));
  };

  const changeSorting = (sortBy: string) => {
    setParams((prevParams) => ({
      ...prevParams,
      sortBy,
      desc: prevParams.sortBy !== sortBy ? false : !prevParams.desc,
      page: 1,
    }));
  };

  const columnsConfig = getColumnsConfig(
    i18n,
    params.desc,
    changeSorting,
    params.sortBy,
  );
  const dataGridRef = useRef<DataGridRef<any, number>>(null);
  useDataGrid();

  const paginationProps = {
    page: params.page,
    perPage: params.perPage,
    totalCount,
    pageSizes: pageSizes,
    numberOfRows: numberOfRows,
    changePagination: changePagination,
  };

  const resetFilters = () => {
    form.reset({
      invoiceNumber: '',
      debt: false,
      isOverdueDebt: false,
      credited: false,
      repaid: false,
    });

    void form.handleSubmitDefault();
  };

  const errObj = outstandingReceivablesData?.error as ErrObj;
  const isExternalServiceUnavailable =
    errObj?.response?.type === abasUnavailableUrn;

  const isPlaceholderVisible =
    !data?.records.length &&
    !outstandingReceivablesData.isLoading &&
    !outstandingReceivablesData.isPlaceholderData &&
    !isExternalServiceUnavailable;

  return (
    <div data-test-id={'outstanding-receivables-container'}>
      <Breadcrumbs />
      <Loading
        loading={
          outstandingReceivablesData.isLoading ||
          outstandingReceivablesData.isPlaceholderData
        }
      >
        <form onSubmit={form.handleSubmitDefault}>
          <div className={styles.filterRowContainer}>
            <Input
              testId={'invoice-number-input'}
              className={clsx(styles.width180)}
              leftHelperText={i18n.t('invoice')}
              {...form.register('invoiceNumber')}
              endAdornment={
                <div>
                  {invoiceNumber && (
                    <IconButton
                      type={'destroy'}
                      onClick={() => form.setValue('invoiceNumber', '')}
                    >
                      <Cross />
                    </IconButton>
                  )}
                </div>
              }
              endAdornmentClassname={styles.endAdornmentClassname}
            />
            <div className={styles.greyText}>{i18n.t('status')}</div>
            <div className={styles.checkboxRowContainer}>
              <Checkbox
                isChecked={isOverdueDebt}
                onChange={(e) => {
                  form.setValue('isOverdueDebt', e.target.checked);
                }}
                actionText={i18n.t('overdue_debt')}
                testId={'overdue-debt-checkbox'}
              />
            </div>
            <div className={styles.checkboxRowContainer}>
              <Checkbox
                isChecked={debt}
                onChange={(e) => {
                  form.setValue('debt', e.target.checked);
                }}
                actionText={i18n.t('debt')}
                testId={'debt-checkbox'}
              />
            </div>
            <div className={styles.checkboxRowContainer}>
              <Checkbox
                isChecked={repaid}
                onChange={(e) => {
                  form.setValue('repaid', e.target.checked);
                }}
                actionText={i18n.t('repaid')}
                testId={'repaid-checkbox'}
              />
            </div>
            <div className={styles.checkboxRowContainer}>
              <Checkbox
                isChecked={credited}
                onChange={(e) => {
                  form.setValue('credited', e.target.checked);
                }}
                actionText={i18n.t('credited')}
                testId={'credited-checkbox'}
              />
            </div>
            <Button
              type={'submit'}
              className={clsx(styles.actionButton)}
              title={i18n.t('apply')}
              testId={'filters-apply-button'}
            />
            {isResetButtonShown && (
              <NegativeActionText
                text={i18n.t('reset')}
                onClickHandler={resetFilters}
                testId={'filters-reset-button'}
              />
            )}
          </div>
        </form>
        {!isExternalServiceUnavailable && (
          <DXG.DataGrid
            ref={dataGridRef}
            dataSource={data?.records}
            className={clsx(
              gridStyles.editorOutline,
              formStyles.table,
              styles.fix,
            )}
            noDataText={''}
            sorting={{ mode: 'none' }}
            paging={{ pageSize: 150 }}
            onContextMenuPreparing={onContextMenuPreparing}
            onCellClick={(event) => headerSortClick(event, changeSorting)}
            onCellPrepared={(e) => {
              if (e.rowType === 'data' && e.column.dataField === Open) {
                if (e.value > 0) {
                  e.cellElement.classList.add(styles.backgroundRed);
                }
              }

              if (
                e.rowType === 'data' &&
                e.column.dataField === InvoiceAmount
              ) {
                if (e.value < 0) {
                  e.cellElement.classList.add(styles.backgroundGreen);
                }
              }

              if (e.rowType === 'data' && e.column.dataField === DueDate) {
                // need this because e.values is not in type but exist
                // @ts-ignore
                const rowValues = e.values as any[];

                const openInvoice = rowValues?.[4] as number;

                if (e.value && isPast(new Date(e.value)) && openInvoice > 0) {
                  e.cellElement.classList.add(styles.backgroundRed);
                }
              }
            }}
          >
            <Scrolling
              columnRenderingMode={'virtual'}
              showScrollbar={'always'}
            />
            {columnsConfig.map((x) => {
              return (
                <DXG.Column
                  cssClass={clsx(x.cssClass, formStyles.text)}
                  key={x.dataField}
                  showEditorAlways={x.allowEditing}
                  headerCellRender={(cellInfo) => (
                    <TableHeaderCell
                      dataField={cellInfo.column.dataField}
                      caption={cellInfo.column.caption}
                      sortBy={params.sortBy}
                      desc={params.desc}
                    />
                  )}
                  {...x}
                />
              );
            })}
            <Pager visible={false} />
          </DXG.DataGrid>
        )}
        {isPlaceholderVisible && <TablePlaceholder />}
        {isExternalServiceUnavailable && (
          <AbasPlaceholder containerClassname={styles.marginTop152} />
        )}
        {!!numberOfRows && (
          <>
            <Total totalSum={totalSum} generalStatus={generalStatus} />
            <AppPagination {...paginationProps} />
          </>
        )}
      </Loading>
    </div>
  );
};
